import { useTranslation } from "react-i18next";
import { Avatar } from "@/atoms/avatar";
import { displayName, type Seller } from "@/utils/coach";

/**
 * The coach header (checkout 1.6 region `coachHeader`, checkout-form 1.4 anatomy).
 *
 * The first thing inside the sheet is the seller: the workspace that owns the payment account, a
 * team under its square mark or a coach under his round photo, at 48, the words "Coaching by" as
 * a caption, and the seller's name at h3 beside it, the same at every width. Gymkee is not in
 * this row: above the sheet is the Gymkee Pay bar (gate 20.3, the money-page exemption), and
 * inside the sheet Gymkee is named once more, in the consent line.
 *
 * The name is never upper-cased and never truncated; only a shouted surname is title-cased for
 * display (displayName), and a company form like SASU survives. A long name wraps onto a second
 * line, which is why the language control lives in the Gymkee Pay bar and not in this row: it
 * used to share it, and at 480 the name lost.
 *
 * The header opens `main`; the Gymkee Pay bar is the page's one banner landmark.
 */

export interface CoachHeaderProps {
  seller: Seller;
}

export function CoachHeader({ seller }: CoachHeaderProps) {
  const { name, src, shape } = seller;
  const { t } = useTranslation();

  return (
    <div className="gap-gk-4 flex items-center">
      <Avatar name={name} src={src} size={48} variant={shape} />
      <div className="gap-gk-1 flex min-w-0 flex-1 flex-col">
        <span className="gk-caption text-[var(--gk-text-secondary)]">
          {t("gymkeePay.checkout.coachingBy")}
        </span>
        {/* Drawn at heading size and NOT a heading: the product's own h1 is the first heading on
            the page (checkout 1.6 headingOrder), and an h2 above it would make the seller's name
            the document's outline instead of the thing being bought. */}
        <p className="gk-h3 break-words text-[var(--gk-text-primary)]">
          {displayName(name)}
        </p>
      </div>
    </div>
  );
}
